import { useEffect, useState } from 'react'
import { catalogEndpoints } from '@/core/api/endpoints'
import type { CatServico } from '@/core/db/entities'
import { servicoDtoToEntity } from '@/core/db/mappers'
import { gravarCache, lerCache } from './carreteiroCache'

/**
 * Serviços que o caminhão pode lançar na data: itens de contrato forma 'C'
 * vigentes (mesma consulta com que o SIGA aceita a viagem). Cada serviço vem
 * com item de contrato, garantia, preço e datas — a prévia de produção/valor
 * funciona offline e o servidor recalcula no envio.
 *
 * Offline: usa os itens em cache do caminhão, filtrados pela data da ficha.
 * Sem cache não há lista (evita oferecer serviço que o servidor recusaria).
 * Serviços inativos nunca aparecem na lista.
 */

interface ServicosCache {
  items: CatServico[]
}

export interface CarreteiroServicosState {
  servicos: CatServico[]
  loading: boolean
  error: string | null
  source: 'live' | 'cache' | null
}

const VAZIO: CarreteiroServicosState = { servicos: [], loading: false, error: null, source: null }

function ativosNaData(items: CatServico[], dt: string): CatServico[] {
  return items
    .filter((s) => !s.inativo)
    .filter(
      (s) =>
        (s.contratoDtInicio == null || s.contratoDtInicio <= dt) &&
        (s.contratoDtFinal == null || s.contratoDtFinal >= dt),
    )
    .sort((a, b) => a.nome.localeCompare(b.nome))
}

export function useServicosCarreteiroPorMve(
  mveId: number | null | undefined,
  dt: string,
): CarreteiroServicosState {
  const [state, setState] = useState<CarreteiroServicosState>(VAZIO)

  useEffect(() => {
    if (mveId == null) {
      setState(VAZIO)
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))

    ;(async () => {
      const key = `servicos:${mveId}`
      const previo = await lerCache<ServicosCache>(key)
      if (cancelled) return
      if (previo) setState({ servicos: ativosNaData(previo.items, dt), loading: true, error: null, source: 'cache' })
      try {
        const res = await catalogEndpoints.servicosCarreteiroPorMve(mveId, dt)
        if (cancelled) return
        const items = (res.data?.items ?? []).map(servicoDtoToEntity)
        void gravarCache(key, { items } satisfies ServicosCache)
        setState({ servicos: ativosNaData(items, dt), loading: false, error: null, source: 'live' })
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        const cache = await lerCache<ServicosCache>(key)
        if (cancelled) return
        setState(
          cache
            ? { servicos: ativosNaData(cache.items, dt), loading: false, error: msg, source: 'cache' }
            : { ...VAZIO, error: msg },
        )
      }
    })()

    return () => {
      cancelled = true
    }
  }, [mveId, dt])

  return state
}
